import React, { useEffect, useState } from 'react'
import { Box, Button, IconButton, Link, Stack, Typography } from '@mui/material'
import { object, string } from 'yup'
import { AxiosError } from 'axios'
import { useFormContext } from 'react-hook-form'

import { Dialog, Icon, InfoBox, useSnackbarContext } from '../components'
import { useMutateSendUrl } from '../services'
import { ApiErrorResponse } from '../types.ts'

import {
  Form,
  FormSchema,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  SubmitButton,
  TextFieldControl,
} from './form'

const SEND_URL_FORM_NAME = 'sendUrlForm'
interface SendUrlFormState {
  clientEmail: string
  clientPhone: string
}
const sendUrlFormValidationSchema = object<FormSchema<SendUrlFormState>>({
  clientEmail: string().test(function (value) {
    const { clientPhone } = this.parent
    if (!clientPhone) {
      if (!value) {
        return this.createError({ message: FormSchemaMessages.required })
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (value && !FormSchemaTests.isEmail.test(value)) {
      return this.createError({ message: FormSchemaTests.isEmail.message })
    }

    return true
  }),
  clientPhone: string().test(function (value) {
    const { clientEmail } = this.parent
    if (!clientEmail) {
      if (!value) {
        return this.createError({ message: FormSchemaMessages.required })
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (value && !FormSchemaTests.isPhoneNumber.test(value)) {
      return this.createError({ message: FormSchemaTests.isPhoneNumber.message })
    }

    return true
  }),
})

const EmailField = (): React.ReactElement => {
  const { trigger } = useFormContext<SendUrlFormState>()

  useEffect(() => {
    trigger('clientEmail')
  }, [])

  return (
    <TextFieldControl<SendUrlFormState>
      name="clientEmail"
      fullWidth
      label="Email клиента"
      revalidateFields={['clientPhone']}
    />
  )
}

export const SendUrlButton = (props: {
  id: string | number
  clientEmail?: string
  url: string
  sendUrl: string
  title?: string
}): React.ReactElement => {
  const [showSendUlr, setShowSendUrl] = useState(false)
  const handleTogglePopup = (visible: boolean) => () => setShowSendUrl(visible)
  const { reset, mutateAsync } = useMutateSendUrl(props.id, props.sendUrl)
  const { addSnack } = useSnackbarContext()

  const handleSendUrlMutation = async (formValues: SendUrlFormState) => {
    try {
      const response = await mutateAsync(formValues)
      const isSuccess = response.data === 'OK'
      addSnack({
        message: isSuccess
          ? 'Ссылка на страницу оплаты успешно отправлена'
          : 'Произошла ошибка при отправке url, попробуйте ещё раз',
        severity: isSuccess ? 'success' : 'error',
        timeout: isSuccess ? 6000 : 3000,
        closeIcon: false,
      })

      if (isSuccess) {
        setShowSendUrl(false)
        reset()
      }
    } catch (axiosError) {
      let errorMessage = 'Произошла непредвиденная ошибка'

      const error = axiosError as AxiosError<ApiErrorResponse>
      if (error.isAxiosError && error.response?.data?.errorDetails) {
        errorMessage = error.response.data.errorDetails
      }

      addSnack({
        message: errorMessage,
        severity: 'error',
        timeout: 3000,
      })
    }
  }

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {props.title ? (
        <Button fullWidth size="medium" color="primary" onClick={handleTogglePopup(true)}>
          {props.title}
        </Button>
      ) : (
        <IconButton color="primary" onClick={handleTogglePopup(true)}>
          <Icon name="Arrow45" />
        </IconButton>
      )}
      {showSendUlr && (
        <Form<SendUrlFormState>
          name={SEND_URL_FORM_NAME}
          validationSchema={sendUrlFormValidationSchema}
          initialValues={{
            clientEmail: props.clientEmail,
          }}
          onSubmit={handleSendUrlMutation}
        >
          <Dialog
            open
            title={`Отправить ссылку на оплату №${props.id}`}
            onClose={handleTogglePopup(false)}
            content={
              <>
                <InfoBox>
                  <Typography mb={2}>
                    При нажатии на кнопку "Отправить" будет сформировано и отправлено письмо со ссылкой на страницу
                    оплаты на электронный адрес или номер телефона, указанных в соответствующих полях.
                    <br />
                  </Typography>
                  <Typography display="inline">Ссылка: </Typography>
                  <Link href={props.url} underline="hover" variant="body1" target="_blank">
                    {props.url}
                  </Link>
                </InfoBox>
                <Stack gap={2}>
                  <EmailField />
                  <TextFieldControl<SendUrlFormState>
                    name="clientPhone"
                    fullWidth
                    label="Номер телефона клиента"
                    revalidateFields={['clientEmail']}
                    normalize={InputNormalizers.digitsOnly}
                  />
                </Stack>
              </>
            }
            actions={
              <>
                <SubmitButton form={SEND_URL_FORM_NAME}>Отправить ссылку</SubmitButton>
                <Button fullWidth color="secondary" size="large" onClick={handleTogglePopup(false)}>
                  Отмена
                </Button>
              </>
            }
          />
        </Form>
      )}
    </Box>
  )
}
